import React from "react";
import dayjs from "dayjs";
import { useAppDispatch, useAppSelector } from "../../../Core/redux/hooks";
import { updateConversations } from "../../../Core/redux/slices/conversations.slice";
import CustomAvatar from "../../../Core/Common/CustomAvatar";
import ParseMentions from "../ParseMentions";
import { ReadReceiptsIcon } from "../../../Core/svgV3/ReadReceipts";
import { DoubleTickIcon, SingleTickIcon } from "../../../Core/svgV3/TickIcon";

const CloseIcon = ({ className = "" }) => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
        <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
);

const getEntryTimestamp = (entry) =>
    entry?.readAt || entry?.receivedAt || entry?.timestamp || entry?.time || entry?.createdAt || null;

// Splits into a date part ("Today" / "Yesterday" / "MMM DD") and a time part ("hh:mm A")
// so they can be styled separately, matching the rendered Figma reference.
const formatTimestampParts = (ts) => {
    if (!ts) return null;
    const d = dayjs(ts);
    const datePart = d.isToday() ? "Today" : d.isYesterday() ? "Yesterday" : d.format("MMM DD");
    const timePart = d.format("hh:mm A");
    return { datePart, timePart };
};

// Timestamp text — Figma: Outfit, Weight 300, 12px/18px, letter-spacing 0%, align right.
// Date portion ("Today"/"Yesterday") is Gray/500 #667085; the time portion renders black #292927,
// matching the rendered "Today 05:42 PM" reference where only the date label is greyed out.
const Timestamp = ({ value }) => {
    const parts = formatTimestampParts(value);
    if (!parts) {
        return (
            <span className="out-300-12 text-[#667085] flex-shrink-0 whitespace-nowrap text-right tracking-normal">
                -
            </span>
        );
    }
    return (
        <span className="out-300-12 flex-shrink-0 whitespace-nowrap text-right tracking-normal">
            <span className="text-[#667085]">{parts.datePart} </span>
            <span className="text-[#292927]">{parts.timePart}</span>
        </span>
    );
};

const PersonRow = ({ member, entry }) => (
    <div className="flex items-center justify-between py-2">
        <div className="flex items-center gap-x-2 min-w-0">
            <CustomAvatar
                title={member ? `${member.firstname ?? ""} ${member.lastname ?? ""}`.trim() : "Unknown"}
                size={28}
                src={member?.profilePicture}
                className="flex-shrink-0"
            />
            {/* Name — Outfit, Weight 500, 14px/20px, letter-spacing 0%, Black #292927 */}
            <span className="out-500-14 text-[#292927] tracking-normal truncate">
                {member ? `${member.firstname ?? ""} ${member.lastname ?? ""}`.trim() : "Unknown user"}
            </span>
        </div>
        <Timestamp value={getEntryTimestamp(entry)} />
    </div>
);

// DM summary row — "Read" / "Delivered" with just a timestamp, no name list,
// since in a 1:1 there's only ever the other participant.
// Matches Figma spec: horizontal, fill width, hug height, 7px/16px padding,
// 1px bottom border (#DDE5ED), space-between.
// Label — Outfit, Weight 500, 14px/20px, letter-spacing 0%, Gray/600 #475467
const StatusRow = ({ icon, label, timestamp }) => (
    <div className="flex items-center justify-between py-[7px] px-4 border-b border-[#DDE5ED] last:border-b-0">
        <div className="flex items-center gap-x-2">
            {icon}
            <span className="out-500-14 text-[#475467] tracking-normal">{label}</span>
        </div>
        <Timestamp value={timestamp} />
    </div>
);

const MessageInfoDrawer = () => {
    const dispatch = useAppDispatch();
    const { messageInfoDrawer, currentThread } = useAppSelector((state) => state.conversations);
    const { tenantMembers } = useAppSelector((state) => state.tenant);
    const { user } = useAppSelector((state) => state.userDetails);

    const visible = messageInfoDrawer?.visible;
    const message = messageInfoDrawer?.message;

    if (!visible || !message) return null;

    const isGroup = currentThread?.type === "group";

    // Defensive: never list/count the current user in either set, group or DM.
    const excludeSelf = (arr) => arr.filter((r) => String(r.userId) !== String(user?.id));

    const readBy = excludeSelf(Array.isArray(message.messageReadBy) ? message.messageReadBy : []);
    const receivedBy = excludeSelf(Array.isArray(message.messageReceivedBy) ? message.messageReceivedBy : []);

    const readUserIds = new Set(readBy.map((r) => String(r.userId)));
    // "Delivered to" = received but not yet read, matching the read-receipt tick logic elsewhere in Message.jsx
    const deliveredOnly = receivedBy.filter((r) => !readUserIds.has(String(r.userId)));

    const findMember = (userId) => tenantMembers?.find((t) => String(t.id) === String(userId));

    const handleClose = () =>
        dispatch(updateConversations({ key: "messageInfoDrawer", value: { visible: false, message: null } }));

    return (
        <div className="flex flex-col h-full min-h-0 border-l border-gray-100 bg-white w-[320px] flex-shrink-0">
            <div className="flex items-center gap-x-2 px-4 py-3">
                <button onClick={handleClose} className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-700 flex-shrink-0">
                    <CloseIcon />
                </button>
                <span className="out-500-14 text-[#292927] tracking-normal">Message Info</span>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 ">
                {/* Message preview bubble */}
                <div className="relative group bg-primary-600 border border-primary-600 text-white rounded-[12px] px-3 py-2 transition-all duration-100 ease-out hover:bg-[#194185] hover:border-[#0F2B5C]">
                    <div className="flex items-center justify-between mb-1 gap-x-2">
                        <span className="out-500-12 text-white">You</span>
                        <div className="flex items-center gap-x-1 flex-shrink-0">
                            <span className="out-300-10 text-white/60">
                                {message.createdAt ? dayjs(message.createdAt).utc().local().format("h:mm A") : "-"}
                            </span>
                            {readBy.length === 0 && receivedBy.length === 0 ? (
                                <SingleTickIcon style={{ color: "#F9FBFE" }} />
                            ) : (
                                <DoubleTickIcon style={{ color: readBy.length > 0 ? "#FEB273" : "#F9FBFE" }} />
                            )}
                        </div>
                    </div>
                    <div className="[&_*]:!text-white">
                        <ParseMentions value={message.message || message.content} className="text-white" />
                    </div>
                </div>

                {isGroup ? (
                    <>
                        {/* Read by */}
                        {readBy.length > 0 && (
                            <div className="mt-4">
                                <div className="out-500-12 text-gray-400 mb-1">Read by</div>
                                <div className="divide-y divide-gray-100">
                                    {readBy.map((entry) => (
                                        <PersonRow key={entry.userId} member={findMember(entry.userId)} entry={entry} />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Delivered to */}
                        {deliveredOnly.length > 0 && (
                            <div className="mt-4">
                                <div className="out-500-12 text-gray-400 mb-1">Delivered to</div>
                                <div className="divide-y divide-gray-100">
                                    {deliveredOnly.map((entry) => (
                                        <PersonRow key={entry.userId} member={findMember(entry.userId)} entry={entry} />
                                    ))}
                                </div>
                            </div>
                        )}

                        {readBy.length === 0 && deliveredOnly.length === 0 && (
                            <div className="mt-4 out-300-12 text-gray-400">Not delivered to anyone yet.</div>
                        )}
                    </>
                ) : (
                    <div className="mt-4 rounded-lg bg-gray-50 overflow-hidden">
                        {readBy.length > 0 && (
                            <StatusRow
                                icon={<ReadReceiptsIcon style={{ color: "#FEB273" }} />}
                                label="Read"
                                timestamp={getEntryTimestamp(readBy[0])}
                            />
                        )}
                        {receivedBy.length > 0 && (
                            <StatusRow
                                icon={<ReadReceiptsIcon style={{ color: "#98A2B3" }} />}
                                label="Delivered"
                                timestamp={getEntryTimestamp(receivedBy[0])}
                            />
                        )}
                        {readBy.length === 0 && receivedBy.length === 0 && (
                            <div className="py-[7px] px-4 out-300-12 text-gray-400">Not delivered yet.</div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MessageInfoDrawer;